import { z } from 'zod'

import { AccessModifierToStudentSchema, ShareScopeSchema } from '~/@entities/Content'
import { HomeworkSchema } from '~/@entities/Homework'
import { ProblemSchema, ProblemSourceTypeSchema, TaggedProblemSchema } from '~/@entities/Problem'
import { AllGradeSubjectSchema, RevisionSchema, SchoolTypeSchema } from '~/@entities/SchoolSystem'
import { StudentIdSchema, StudentStatusSchema } from '~/@entities/Student'

/***************** 반응형 평가 종류 *****************/
// 서버 CasType. 크레버스 입학고사(비재원생 대상)와 일반 자동시험
export const CAS_TYPE = ['ENTRANCE_EXAM', 'DEFAULT'] as const

/***************** 응시 상태 *****************/
export const CAS_ASSIGNMENT_STATUS = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED'] as const

/***************** 문항 채점 결과 *****************/
// 자동시험은 모르겠음 제출도 오답으로 채점해 두 값만 있다
export const CAS_SCORING_RESULT = ['CORRECT', 'WRONG'] as const

/***************** 출제 범위 단위 *****************/
export const CAS_FIELD_REFERENCE_TYPE = ['BIG_CHAPTER'] as const

export const CasTypeSchema = z.enum(CAS_TYPE)
export type CasType = z.infer<typeof CasTypeSchema>

export const CasAssignmentStatusSchema = z.enum(CAS_ASSIGNMENT_STATUS)
export type CasAssignmentStatus = z.infer<typeof CasAssignmentStatusSchema>

export const CasScoringResultSchema = z.enum(CAS_SCORING_RESULT)
export type CasScoringResult = z.infer<typeof CasScoringResultSchema>

export const CasFieldReferenceTypeSchema = z.enum(CAS_FIELD_REFERENCE_TYPE)
export type CasFieldReferenceType = z.infer<typeof CasFieldReferenceTypeSchema>

const _localDatetime = z.iso.datetime({ local: true })

/***************** 시험지 *****************/

// 시험지 메타. 목록·출제 묶음·학생 시점 응답이 같은 이름으로 싣는다.
const _CasMetaSchema = z.object({
  casId: z.number(),
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  title: z.string(),
  // 시험 범위(대단원) 표기
  field: z.string(),
  // 배정자 표기명
  writer: z.string(),
  maxProblemCount: z.number(),
  autoScored: z.boolean(),
  // 자동시험은 정책상 학원 공유(ACADEMY)만 만들지만 서버 어휘 전체로 받는다
  shareScope: ShareScopeSchema,
  accessModifierToStudent: AccessModifierToStudentSchema,
})

// 시험지 목록의 배정 학생 한 명. studentWorksheetId 자리에 배정 id(assigned_cas.id)가 들어간다.
export const CasAssigneeSchema = z.object({
  studentWorksheetId: z.number(),
  studentId: StudentIdSchema,
  studentName: z.string(),
  studentStatus: StudentStatusSchema,
  status: CasAssignmentStatusSchema,
})
export type CasAssignee = z.infer<typeof CasAssigneeSchema>

/** 시험지 목록(GET /cas) 한 행 */
export const CasSchema = _CasMetaSchema.extend({
  createDatetime: _localDatetime,
  assignList: z.array(CasAssigneeSchema),
})
export type Cas = z.infer<typeof CasSchema>

// 출제 범위 한 칸. trieKey 는 그 대단원이 속한 학년·학기 경로까지 담는다(`...학년학기과목.대단원`).
export const CasFieldSchema = z.object({
  referenceType: CasFieldReferenceTypeSchema,
  referenceId: z.number(),
  trieKey: z.string().nullable(),
})
export type CasField = z.infer<typeof CasFieldSchema>

/** 시험지 상세 — 수정·복제 진입 시 폼을 채운다. 자동시험과 입학고사가 같은 모양이다 */
export const CasDetailSchema = z.object({
  casId: CasSchema.shape.casId,
  type: CasTypeSchema,
  title: z.string(),
  schoolType: SchoolTypeSchema,
  revision: RevisionSchema,
  grade: AllGradeSubjectSchema,
  maxProblemCount: z.number(),
  fields: z.array(CasFieldSchema),
})
export type CasDetail = z.infer<typeof CasDetailSchema>

/***************** 재원생 배정 *****************/

export const CasAssignedStudentSchema = z.object({
  assignedId: z.number(),
  studentHomeworkId: z.number().nullable(),
  studentId: StudentIdSchema,
  studentName: z.string(),
  studentStatus: StudentStatusSchema,
  status: CasAssignmentStatusSchema,
  // n등분 점수. 응시 완료 전이면 null
  score: z.number().nullable(),
})
export type CasAssignedStudent = z.infer<typeof CasAssignedStudentSchema>

/** 반·학년 시점 출제 묶음 한 행 — 같은 시험지를 받은 학생들의 응시 정보가 함께 온다 */
export const GroupAssignedCasSchema = _CasMetaSchema.extend({
  // 묶음 중 가장 최근 출제 일시
  assignDatetime: _localDatetime,
  // 전부 미응시면 NOT_STARTED, 전부 완료면 COMPLETED, 그 밖은 IN_PROGRESS
  status: CasAssignmentStatusSchema,
  // 전원 완료일 때만 평균 점수
  score: z.number().nullable(),
  assignedStudentList: z.array(CasAssignedStudentSchema),
})
export type GroupAssignedCas = z.infer<typeof GroupAssignedCasSchema>

// 출제 한 건의 응시 진행
const _CasProgressSchema = z.object({
  assignDatetime: _localDatetime,
  startedAt: _localDatetime.nullable(),
  completedAt: _localDatetime.nullable(),
  status: CasAssignmentStatusSchema,
  score: z.number().nullable(),
  scoredCount: z.number(),
  correctCount: z.number(),
})

/** 한 학생 시점 출제 한 건(선생님 화면) */
export const StudentAssignedCasSchema = _CasMetaSchema.merge(_CasProgressSchema).extend({
  assignedId: CasAssignedStudentSchema.shape.assignedId,
  studentHomeworkId: z.number().nullable(),
  // 크레버스 학원의 보고서 공유 여부. 크레버스가 아니면 null
  creverseShared: z.boolean().nullable(),
})
export type StudentAssignedCas = z.infer<typeof StudentAssignedCasSchema>

/***************** 채점 *****************/

/** 선생님 채점 화면의 문제 — 문제 본체에 출처가 붙는다 */
export const CasScoringProblemSchema = ProblemSchema.extend({
  sourceType: ProblemSourceTypeSchema,
})
export type CasScoringProblem = z.infer<typeof CasScoringProblemSchema>

const _CasAnswerSchema = z.object({
  scoringId: z.number(),
  orderingNumber: z.number(),
  result: CasScoringResultSchema,
  // 다중 셀 답은 userAnswers 로만 오고 이 필드는 비어 있다
  userAnswer: z.string().nullable(),
  // 초·중·고 키패드 다중 셀 답. null 원소는 미입력 빈칸이다
  userAnswers: z.array(z.string().nullable()).nullable(),
})

export const CasScoringItemSchema = _CasAnswerSchema.extend({
  problem: CasScoringProblemSchema,
})
export type CasScoringItem = z.infer<typeof CasScoringItemSchema>

/** 반·학년 채점 화면의 학생 한 명 */
export const CasStudentScoringSchema = z.object({
  studentId: StudentIdSchema,
  studentName: z.string(),
  assignedCasId: CasAssignedStudentSchema.shape.assignedId,
  status: CasAssignmentStatusSchema,
  score: z.number().nullable(),
  scoringList: z.array(CasScoringItemSchema),
})
export type CasStudentScoring = z.infer<typeof CasStudentScoringSchema>

/***************** 입학고사 *****************/

/** 입학고사 시험지에 배정된 비재원생 한 명. (creverseGuestId, admissionRefId) 로 배정 모달의 행과 짝짓는다 */
export const EntranceCasAssigneeSchema = z.object({
  guestEntranceCasId: z.number(),
  creverseGuestId: z.number(),
  admissionRefId: z.string(),
  guestName: z.string(),
  status: CasAssignmentStatusSchema,
})
export type EntranceCasAssignee = z.infer<typeof EntranceCasAssigneeSchema>

/** 입학고사 시험지 목록 한 행 — 배정 대상이 비재원생이라 배정 목록 모양만 다르다 */
export const EntranceCasSchema = CasSchema.extend({
  assignList: z.array(EntranceCasAssigneeSchema),
})
export type EntranceCas = z.infer<typeof EntranceCasSchema>

/** 비재원생 한 명에게 배정된 입학고사 한 건(배정 현황과 응시 결과) */
export const GuestEntranceCasSchema = _CasMetaSchema.merge(_CasProgressSchema).extend({
  guestEntranceCasId: EntranceCasAssigneeSchema.shape.guestEntranceCasId,
  admissionRefId: z.string(),
  // 배정이 가리키는 시험 회차명. 회차가 연결되지 않았으면 null
  admissionName: z.string().nullable(),
  // 보고서 공개 여부. 공유 전이면 null, 공개는 true, 회수는 false
  reportIsOpen: z.boolean().nullable(),
})
export type GuestEntranceCas = z.infer<typeof GuestEntranceCasSchema>

/***************** 학생 응시 *****************/

// 학생이 푸는 문제(서버 CasProblemDetailResponse). 유형 이름·태그가 붙는다. 함께 오는 levelOfConceptChip·
// sourceType·index·favorite 는 학생 응시 화면이 읽지 않아 받지 않는다.
export const CasProblemSchema = TaggedProblemSchema
export type CasProblem = z.infer<typeof CasProblemSchema>

/** 학생 숙제 목록의 출제 한 건 */
export const CasHomeworkAssignmentSchema = _CasMetaSchema.merge(_CasProgressSchema).extend({
  assignedId: z.number(),
  wrongCount: z.number(),
  homeworks: z.array(HomeworkSchema),
})
export type CasHomeworkAssignment = z.infer<typeof CasHomeworkAssignmentSchema>

/** 응시 화면 진입. 미응시면 서버가 응시를 시작하고 첫 문제를 싣는다. 완료면 problem 이 null 이다 */
export const CasAssignmentDetailSchema = _CasMetaSchema.merge(_CasProgressSchema).extend({
  assignedId: z.number(),
  problem: CasProblemSchema.nullable(),
})
export type CasAssignmentDetail = z.infer<typeof CasAssignmentDetailSchema>

/** 학생 결과 화면의 문항 한 개 */
export const CasSolvedProblemSchema = _CasAnswerSchema.extend({
  problem: CasProblemSchema,
  handwrittenNoteUrl: z.string().nullable(),
})
export type CasSolvedProblem = z.infer<typeof CasSolvedProblemSchema>

/** 답 제출 결과. 마지막 문제를 내면 status 가 COMPLETED 가 되고 problem 이 null 이다 */
export const CasSubmitResultSchema = z.object({
  assignedId: z.number(),
  status: CasAssignmentStatusSchema,
  problem: CasProblemSchema.nullable(),
  score: z.number().nullable(),
  scoredCount: z.number(),
  correctCount: z.number(),
})
export type CasSubmitResult = z.infer<typeof CasSubmitResultSchema>
